// material ui components
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { useCallback, useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";

import { ampli } from "../../../ampli";
import { IUserProperties } from "../../../ampli/models";
import { ampliIncrementEvent } from "../../../ampli/userIdentify";
// libraries
import Alert, { AlertType } from "../../../components/common/WaffyAlert";
// components
import ModalComponent from "../../../components/modal";
import { ALERTMESSAGE } from "../../../enums";
import { remoteConfig } from "../../../firebase/firebase";
import { EUploadImageType } from "../../../s3Integration/enums";
import { uploadFile } from "../../../s3Integration/s3Client";
// apis
import {
  createContractDetails,
  editContractDetails,
  getContractByUrl,
  updateContractById,
} from "../../../setup/api/services/contract";
// typings
import {
  EFeePayee,
  EShipmentType,
  IContractDetailsBody,
  IContractDetailsResponse,
  IContractDetailsResponseData,
  ItemDetailsDTO,
} from "../../../setup/api/services/contract/models";
import { AuthConfig } from "../../../setup/context/config/authConfig";
// helpers
import { contractBody } from "../../../setup/util/helpers";
import AddPriceForm from "./addPriceForm";
import AddProductForm from "./addProducts";
import ChoiceForm from "./choiceForm";
// styles
import styles from "./newTransaction.module.scss";
import SuccessForm from "./successForm";

export enum ALERT {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  NONE = "NONE",
}

interface Props {
  showModal: boolean;
  onClose: () => void;
  fetchAllContract?: () => void;
  editContract?: IContractDetailsResponseData;
  titleLink?: string;
  priceLink?: number;
}

const NewTransaction = ({
  showModal,
  onClose,
  fetchAllContract,
  editContract,
  titleLink,
  priceLink,
}: Props) => {
  const userId = AuthConfig.getUserInfo().id;
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [contractId, setContractId] = useState<string>("");
  const [shortId, setShortId] = useState<string>("");

  // step 2 states
  const [price, setPrice] = useState<IContractDetailsBody["itemPrice"]>("");
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [shippingFee, setShippingFee] =
    useState<IContractDetailsBody["shipment"]["fee"]>("");
  const [showShippingFee, setShowShippingFee] = useState<boolean>(false);
  const [harajLoading, setHarajLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [contractType, setContractType] = useState<
    "General" | "Haraj" | "Landing"
  >("General");
  // const [feePayee, setFeePayee] = useState<ItemDetailsDTO["feePayee"]>(
  //   EFeePayee.PROVIDER
  // );
  // const [inspectionPeriod, setInspectionPeriod] =
  //   useState<ItemDetailsDTO["inspectionPeriod"]>(5);
  // const [deliveryInDays, setdeliveryInDays] =
  //   useState<ItemDetailsDTO["deliveryInDays"]>(5);

  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<ALERT>(ALERT.NONE);
  const { t } = useTranslation();

  const [ioAuxiliary, setIoAuxilairy] = useState<ItemDetailsDTO["title"]>("");
  const [description, setDescription] =
    useState<ItemDetailsDTO["description"]>("");
  const [images, setImages] = useState<File[]>([]);
  const [s3Images, setS3Images] = useState<string[]>([]);
  useEffect(() => {
    if (showModal) ampli.viewedContractWizardPage();
  }, [showModal]);

  useEffect(() => {
    if (alert !== ALERT.NONE) {
      setTimeout(() => {
        setAlert(ALERT.NONE);
        setAlertMessage("");
      }, 3000);
    }
  }, [alert]);

  useEffect(() => {
    if (titleLink && priceLink) {
      setCurrentStep(1);
      setIoAuxilairy(titleLink);
      setPrice(priceLink);
      setContractType("Landing");
    }
  }, [titleLink, priceLink]);

  const setContractDetails = useCallback(
    (editContractDetails: IContractDetailsResponseData) => {
      setCurrentStep(1);
      editContract?.id && setContractId(editContractDetails?.id);
      editContractDetails?.shortId && setShortId(editContractDetails?.shortId);
      setLoading(false);
      setAlert(ALERT.NONE);
      editContractDetails?.itemDetail?.title &&
        setIoAuxilairy(editContractDetails?.itemDetail?.title);
      editContractDetails?.itemDetail?.description &&
        setDescription(editContractDetails?.itemDetail?.description);
      editContractDetails?.itemDetail?.images &&
        setS3Images(editContractDetails?.itemDetail?.images);
      editContractDetails?.itemPrice &&
        setPrice(editContractDetails?.itemPrice);
      setTermsAccepted(true);
      editContractDetails?.shipment?.fee && setShowShippingFee(true);
      editContractDetails?.shipment?.fee &&
        setShippingFee(editContractDetails?.shipment?.fee);
      // editContractDetails?.feePayee &&
      //   setFeePayee(editContractDetails?.feePayee);
      // editContractDetails?.inspectionPeriod &&
      //   setInspectionPeriod(editContractDetails?.inspectionPeriod);
      // editContractDetails?.deliveryInDays &&
      //   setdeliveryInDays(editContractDetails?.deliveryInDays);
    },
    [editContract?.id]
  );

  const setharajDetails = useCallback(
    (harajAdDetails: IContractDetailsResponseData) => {
      setCurrentStep(1);
      setAlert(ALERT.NONE);
      setAlertMessage("");
      harajAdDetails?.itemDetail?.title &&
        setIoAuxilairy(harajAdDetails?.itemDetail?.title?.substring(0, 50));
      harajAdDetails?.itemDetail?.description &&
        setDescription(
          harajAdDetails?.itemDetail?.description?.substring(0, 500)
        );
      harajAdDetails?.itemDetail?.images &&
        setS3Images(harajAdDetails?.itemDetail?.images?.slice(0, 10));
      harajAdDetails?.itemPrice && setPrice(harajAdDetails?.itemPrice);
      setTermsAccepted(false);
      harajAdDetails?.shipment?.fee && setShowShippingFee(true);
      harajAdDetails?.shipment?.fee &&
        setShippingFee(harajAdDetails?.shipment?.fee);
    },
    []
  );

  useEffect(() => {
    if (editContract) {
      setContractDetails(editContract);
    }
  }, [editContract, setContractDetails]);

  const cleanStates = () => {
    setCurrentStep(0);
    setContractId("");
    setShortId("");
    setLoading(false);
    setAlert(ALERT.NONE);
    setS3Images([]);
    setIoAuxilairy("");
    setDescription("");
    setContractType("General");
    setImages([]);
  };
  const onModalClose = useCallback(() => {
    fetchAllContract && fetchAllContract();
    cleanStates();
    onClose();
  }, [fetchAllContract, onClose]);
  const TimeLine = useCallback(() => {
    return (
      <div className={styles["timeline-container"]}>
        <div
          onClick={() => {
            if (currentStep !== 3) {
              setCurrentStep(1);
            }
          }}
          className={classNames(
            currentStep > 1
              ? styles["timeline-step-completed"]
              : styles["timeline-step-active"]
          )}>
          {currentStep > 1 ? (
            <CheckOutlinedIcon style={{ color: "#fff" }} />
          ) : (
            <p className={"subtitle2"}>1</p>
          )}
        </div>
        <p
          className={classNames(
            "p2",
            currentStep !== 1 && "g400",
            currentStep !== 1 && styles["inactive"]
          )}>
          {t("PRODUCT_DETAILS")}{" "}
        </p>
        <div className={styles["divider"]} />
        <div
          onClick={() => {
            if (currentStep !== 3) {
              if (ioAuxiliary !== "") {
                setCurrentStep(2);
              }
            }
          }}
          className={classNames(
            currentStep === 2
              ? styles["timeline-step-active"]
              : currentStep > 2
              ? styles["timeline-step-completed"]
              : styles["timeline-step"]
          )}>
          {currentStep > 2 ? (
            <CheckOutlinedIcon style={{ color: "#fff" }} />
          ) : (
            <p className={"subtitle2"}>2</p>
          )}
        </div>
        <p
          className={classNames(
            "p2",
            currentStep !== 2 && "g400",
            currentStep !== 2 && styles["inactive"]
          )}>
          {t("PRODUCT_PRICE")}
        </p>
        <>
          <div className={styles["divider"]} />
          <div
            className={classNames(
              currentStep === 3
                ? styles["timeline-step-active"]
                : currentStep > 3
                ? styles["timeline-step-completed"]
                : styles["timeline-step"]
            )}>
            {currentStep > 3 ? (
              <CheckOutlinedIcon style={{ color: "#fff" }} />
            ) : (
              <p className={"subtitle2"}>3</p>
            )}
          </div>
          <p
            className={classNames(
              "p2",
              currentStep !== 3 && "g400",
              currentStep !== 3 && styles["inactive"]
            )}>
            {t("POST_TRANSACTION")}
          </p>
        </>
      </div>
    );
  }, [currentStep, ioAuxiliary, t]);

  const handleStepOne = async (
    title: string,
    description: string,
    images: File[]
  ) => {
    setIoAuxilairy(title);
    setDescription(description);
    setImages(images);
    if (title !== "") setCurrentStep(2);
  };

  const uploadImages = useCallback(
    async (contractId: string) => {
      // eslint-disable-next-line prefer-const
      let urls = new Array<string>();
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const url = await uploadFile(
          image,
          EUploadImageType.CONTRACT,
          contractId
        );
        urls.push(url);
      }
      return urls;
    },
    [images]
  );

  const [, setCommission] = useState<number>(0);

  useEffect(() => {
    try {
      fetchAndActivate(remoteConfig)
        .then(() => {
          const result = getValue(remoteConfig, "commission") as unknown as {
            _source: string;
            _value: string;
          };
          /* eslint-disable */
          setCommission(Number(result?._value));
        })
        .catch((err) => {
          setCommission(0);
        });
    } catch (error) {
      setCommission(0);
    }
  }, []);

  const sendAmpliUserPropertiesForCreatedContract = useCallback(() => {
    ampliIncrementEvent(
      userId,
      IUserProperties.TOTAL_NUMBER_OF_CONTRACT_CREATED
    );
  }, [userId]);

  const sendAmpliEventCreatedContract = useCallback(
    (contract: IContractDetailsResponseData, waffyServiceFees: number) => {
      ampli.createdContract({
        contractCreationDate: new Date().toISOString(), // string,
        waffyServiceFees: waffyServiceFees, // number,
        shipmentFees: Number(contract.shipment?.fee || 0), // number,
        numOfproductImageAttachement: images.length, // number,
        ticketSize: Number(contract?.itemPrice), // number,
        isShipmentCompanyEnabled:
          contract.shipment?.type === EShipmentType.SHIPMENT_COMPANY
            ? true
            : false, // boolean,
        productTitle: contract?.itemDetail?.title, // string
        contractId: contract?.shortId,
        contractType: contractType,
      });
    },
    [images.length, contractType]
  );

  const sendAmpliEventEditedContract = useCallback(
    (contractId: IContractDetailsResponseData["shortId"]) => {
      ampli.editedContract({
        contractId: contractId,
      });
    },
    []
  );

  const uploadImagesAfterCreation = useCallback(
    async (contract: IContractDetailsResponseData) => {
      const urls = await uploadImages(contract.id);
      await updateContractById(contract.id, {
        title: contract.itemDetail.title,
        description: contract.itemDetail.description,
        images: [...urls, ...s3Images],
      });
    },
    [s3Images, uploadImages]
  );

  const handleStepTwo = useCallback(
    async (
      price: IContractDetailsBody["itemPrice"],
      termsAccepted: boolean,
      sar: number,
      shippingFee: IContractDetailsBody["shipment"]["fee"],
      showShippingFee: boolean
      // feePayee: EFeePayee,
      // inspectionPeriod: number,
      // deliveryInDays: number
    ) => {
      setPrice(price);
      setTermsAccepted(termsAccepted);
      setShippingFee(shippingFee);
      setShowShippingFee(showShippingFee);
      // setFeePayee(feePayee);
      // setInspectionPeriod(inspectionPeriod);
      // setdeliveryInDays(deliveryInDays);
      setLoading(true);
      if (price && termsAccepted) {
        const shipFee = showShippingFee ? shippingFee : undefined;
        const body = contractBody(
          ioAuxiliary,
          description,
          price,
          shipFee,
          // feePayee,
          // inspectionPeriod,
          // deliveryInDays,
          termsAccepted,
          showShippingFee ? EShipmentType.SHIPMENT_COMPANY : EShipmentType.NONE
        );
        try {
          let response: IContractDetailsResponse;
          if (editContract) {
            response = await editContractDetails(body, editContract?.id);
            sendAmpliEventEditedContract(response?.data?.shortId);
          } else {
            response = await createContractDetails(body);
            if (response.data) {
              sendAmpliEventCreatedContract(response.data, sar);
              sendAmpliUserPropertiesForCreatedContract();
            }
          }
          const contractId = response?.data?.id;
          await uploadImagesAfterCreation(response?.data);
          setAlert(ALERT.SUCCESS);
          setContractId(contractId);
          setShortId(response?.data?.shortId);
          setLoading(false);
          if (editContract) setCurrentStep(3);
        } catch (error: any) {
          Sentry.captureException(error);
          setAlertMessage(error?.error?.message);
          setAlert(ALERT.ERROR);
          setLoading(false);
        }
      }
    },
    [
      description,
      editContract,
      ioAuxiliary,
      sendAmpliEventCreatedContract,
      sendAmpliUserPropertiesForCreatedContract,
      uploadImagesAfterCreation,
    ]
  );

  const handleS3ImageDelete = useCallback(
    (index) => {
      const tempImages = [...s3Images];
      tempImages.splice(index, 1);
      setS3Images(tempImages);
    },
    [s3Images]
  );

  useEffect(() => {
    if (contractId && !editContract) setCurrentStep(3);
  }, [contractId, editContract]);

  const getHarajDetails = useCallback(async (harajLink: string) => {
    try {
      setHarajLoading(true);
      const result = await getContractByUrl(harajLink);
      setContractType("Haraj");
      setharajDetails(result?.data);
      setAlert(ALERT.SUCCESS);
      setHarajLoading(false);
      setCurrentStep(1);
    } catch (error: any) {
      Sentry.captureException(error);
      setAlertMessage(error?.error?.message);
      setAlert(ALERT.ERROR);
      setHarajLoading(false);
    }
  }, []);

  const renderForms = useCallback(() => {
    switch (currentStep) {
      case 0:
        return (
          <ChoiceForm
            handleClose={() => setCurrentStep(1)}
            handleHarajad={getHarajDetails}
            loading={harajLoading}
          />
        );
      case 1:
        return (
          <AddProductForm
            title={ioAuxiliary}
            desc={description}
            imageFiles={images}
            {...(s3Images.length !== 0 && { s3ImageFiles: s3Images })}
            handleStepOne={handleStepOne}
            {...(s3Images.length !== 0 && {
              handleS3ImageDelete: handleS3ImageDelete,
            })}
          />
        );
      case 2:
        return (
          <AddPriceForm
            priceProps={price}
            termsAcceptedProps={termsAccepted}
            shippingFeeProps={shippingFee}
            showShippingFeeProps={showShippingFee}
            loading={loading}
            handleStepTwo={handleStepTwo}
            handleBack={() => setCurrentStep(1)}
            // {...(feePayee && { feePayeeProps: feePayee })}
            // {...(inspectionPeriod && {
            //   inspectionPeriodProps: inspectionPeriod,
            // })}
            // {...(deliveryInDays && { deliveryInDaysProps: deliveryInDays })}
            {...(editContract && { editContract: true })}
          />
        );
      case 3:
        return (
          <SuccessForm
            contractId={contractId}
            onClose={onModalClose}
            shortId={shortId}
            {...(editContract && { editContract: true })}
          />
        );
      default:
        <AddProductForm
          title={ioAuxiliary}
          desc={description}
          imageFiles={images}
          handleStepOne={handleStepOne}
        />;
    }
  }, [
    currentStep,
    getHarajDetails,
    harajLoading,
    ioAuxiliary,
    description,
    images,
    s3Images,
    handleS3ImageDelete,
    price,
    termsAccepted,
    shippingFee,
    showShippingFee,
    loading,
    handleStepTwo,
    contractId,
    onModalClose,
    shortId,
    editContract,
  ]);

  return (
    <ModalComponent
      showModal={showModal}
      closeModal={async () => onModalClose()}
      header={
        editContract ? t("EDIT_TRANSACTION_SAFE") : t("CREATE_TRANSACTION_SAFE")
      }
      showIcon={true}>
      <div className={styles["container"]}>
        {currentStep !== 0 && <TimeLine />}

        {alert !== ALERT.NONE && alert !== ALERT.SUCCESS && (
          <div style={{ width: "100%" }}>
            <Alert type={AlertType.ERROR}>
              {alertMessage || t(ALERTMESSAGE.ERROR)}
            </Alert>
          </div>
        )}
        <>{renderForms()}</>
      </div>
    </ModalComponent>
  );
};

export default NewTransaction;
